import { FolderTree, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { HelpCategory } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { DeleteHelpCategoryDialog } from '../components/delete-help-category-dialog';
import { useHelpCategoryColumns } from '../components/help-category-columns';
import { HelpCategoryDialog } from '../components/help-category-dialog';
import { useHelpCategoryRowActions } from '../components/help-category-row-actions';
import { useHelpCategories } from '../hooks/use-help-categories';

/** `category: null` adds a new one. */
interface Editing {
  category: HelpCategory | null;
}

const EMPTY: readonly HelpCategory[] = [];

function HelpCategories() {
  const { t } = useTranslation('helpCenter');
  const store = useStore();
  const { can } = usePermissions();
  const query = useHelpCategories(store.id);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<HelpCategory | null>(null);
  const canWrite = can('help.write');
  const onEdit = useCallback((category: HelpCategory) => setEditing({ category }), []);
  const columns = useHelpCategoryColumns();
  const rowActions = useHelpCategoryRowActions({ onEdit, onDelete: setDeleting });
  const rows = query.data ?? EMPTY;
  const table = useDataTable({ data: rows, columns, getRowId: (row) => row.id });
  const nextPosition = rows.reduce((max, c) => Math.max(max, c.position + 1), 0);

  const addButton = (
    <Can permission="help.write">
      <Button onClick={() => setEditing({ category: null })}>
        <Plus aria-hidden />
        {t('categories.new')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('categories.title')}
        description={t('categories.description')}
        backTo={{ to: '/s/$storeId/help', params: { storeId: store.id } }}
        backLabel={t('articles.title')}
        actions={addButton}
      />
      {query.isSuccess && rows.length === 0 ? (
        <EmptyState
          icon={FolderTree}
          title={t('categories.empty.title')}
          description={t('categories.empty.description')}
          action={addButton}
        />
      ) : (
        <DataTable
          table={table}
          search={{ label: t('categories.search'), placeholder: t('categories.search') }}
          rowActions={canWrite ? rowActions : undefined}
          onRowClick={canWrite ? onEdit : undefined}
          getRowLabel={(row) => row.name}
          resourceName={{
            singular: t('categories.resource_one'),
            plural: t('categories.resource_other'),
          }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      <HelpCategoryDialog
        storeId={store.id}
        open={editing !== null}
        category={editing?.category ?? null}
        nextPosition={nextPosition}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <DeleteHelpCategoryDialog
        storeId={store.id}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function HelpCategoriesPage() {
  return (
    <FeatureErrorBoundary name="helpCenter.categories">
      <HelpCategories />
    </FeatureErrorBoundary>
  );
}
